import { useCallback, useEffect, useState } from 'react';

import { useSubmitOrder } from '@zenbusiness/application-commons-ecommerce';
import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Stack from '@zenbusiness/zui/Stack';

import useCheckoutStepCompleted from '#client/hooks/clickstream/useCheckoutStepCompleted';
import useOrderCompleted from '#client/hooks/clickstream/useOrderCompleted';
import useFlowState from '#client/hooks/flow/useFlowState';
import useCheckoutExperimentTracking from '#client/hooks/launchdarkly/useCheckoutExperimentTracking';

import {
  AccountCheckoutCard,
  BusinessCheckoutCard,
  CartCheckoutCard,
  PaymentCheckoutCard
} from './components';
import { CHECKOUT } from '../../constants';
import { useCheckoutContext } from '../../hooks/useCheckoutContext';
import { useCheckoutSteps } from '../../hooks/useCheckoutSteps';
import { usePlaceOrder } from '../../hooks/usePlaceOrder';

const { STEP, ACTION } = CHECKOUT;

const stepComponentsMap = Object.freeze({
  [STEP.ACCOUNT]: AccountCheckoutCard,
  [STEP.BUSINESS]: BusinessCheckoutCard,
  [STEP.CART]: CartCheckoutCard,
  [STEP.PAYMENT]: PaymentCheckoutCard
});

function CheckoutCardList(cardProps) {
  const [{ activeStep, paymentMethodType }, dispatch] = useCheckoutContext();
  const { set } = useFlowState();
  const stepOrder = useCheckoutSteps();
  const { submitOrder, status, errors } = useSubmitOrder();
  const placeOrder = usePlaceOrder({ submitOrder });
  const [submittedOrderUuid, setSubmittedOrderUuid] = useState(null);
  const captureException = useCaptureException();
  const trackOrderCompleted = useOrderCompleted();
  const trackCheckoutStepCompleted = useCheckoutStepCompleted();
  const trackExperimentEvents = useCheckoutExperimentTracking();

  useEffect(() => {
    // Show an error message if the order failed
    if (status === useSubmitOrder.STATUS.FAILED && errors?.length) {
      const firstError = errors[0];
      dispatch({
        type: ACTION.PLACE_ORDER_ERROR,
        payload: { message: firstError.message, ...firstError.extensions }
      });
      captureException(firstError);
      return;
    }

    // Notify the user that the order was placed successfully
    if (status === useSubmitOrder.STATUS.SUCCESS) {
      set('orderUuid', submittedOrderUuid);
      trackOrderCompleted({ orderUuid: submittedOrderUuid, paymentMethod: paymentMethodType });
      trackExperimentEvents();
      dispatch({ type: ACTION.PLACE_ORDER_SUCCESS });
    }

    // no-op, order is still pending
  }, [
    captureException,
    dispatch,
    errors,
    paymentMethodType,
    set,
    status,
    submittedOrderUuid,
    trackExperimentEvents,
    trackOrderCompleted
  ]);

  const handleContinue = useCallback(() => {
    trackCheckoutStepCompleted({
      step: stepOrder.indexOf(activeStep) + 1,
      stepName: activeStep
    });
    dispatch({
      type: ACTION.NEXT_STEP,
      stepOrder
    });
  }, [activeStep, dispatch, stepOrder, trackCheckoutStepCompleted]);

  const handlePlaceOrder = useAsyncCallback(
    async (event) => {
      const { cardToken } = event || {};
      const orderUuid = await placeOrder(cardToken);
      if (orderUuid) {
        setSubmittedOrderUuid(orderUuid);
      }
    },
    [placeOrder, trackOrderCompleted]
  );

  return (
    <Stack spacing={1}>
      {stepOrder.map((step) => {
        const CheckoutCard = stepComponentsMap[step];
        const isPaymentStep = step === STEP.PAYMENT;

        if (isPaymentStep) {
          return <CheckoutCard key={step} onPlaceOrder={handlePlaceOrder} {...cardProps} />;
        }

        return <CheckoutCard key={step} onContinue={handleContinue} {...cardProps} />;
      })}
    </Stack>
  );
}

export default CheckoutCardList;
